import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { dateFormatter } from "../../utils/dateFormatter";

/* ---------------- ADAPTER ---------------- */

const couponsListAdapter = createEntityAdapter({
  selectId: (coupon) => coupon.id,
});

const initialState = couponsListAdapter.getInitialState();

/* ---------------- API SLICE ---------------- */

export const couponsListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCouponsList: builder.query({
      query: ({ search, status } = {}) => {
        let url = "coupons";
        const params = [];

        if (search) params.push(`search=${encodeURIComponent(search)}`);
        if (status) params.push(`status=${encodeURIComponent(status)}`);

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }

        return url;
      },

      transformResponse: (responseData) => {
        const coupons =
          responseData.data?.map((coupon) => ({
            ...coupon,

            start_date:
              coupon.start_date !== null && coupon.start_date !== undefined
                ? dateFormatter(coupon.start_date)
                : "N/A",

            end_date:
              coupon.end_date !== null && coupon.end_date !== undefined
                ? dateFormatter(coupon.end_date)
                : "N/A",
          })) ?? [];

        return couponsListAdapter.setAll(initialState, coupons);
      },

      providesTags: (result) =>
        result
          ? [
              { type: "coupons", id: "LIST" },
              ...result.ids.map((id) => ({ type: "coupons", id })),
            ]
          : [{ type: "coupons", id: "LIST" }],
    }),

    getCouponById: builder.query({
      query: (id) => `coupons/${id}`,
      transformResponse: (res) => {
        const coupon = res.data;

        return {
          ...coupon,
          start_date:
            coupon.start_date !== null && coupon.start_date !== undefined
              ? dateFormatter(coupon.start_date)
              : "N/A",
          end_date:
            coupon.end_date !== null && coupon.end_date !== undefined
              ? dateFormatter(coupon.end_date)
              : "N/A",
        };
      },
      providesTags: (r, e, id) => [{ type: "coupons", id }],
    }),
  }),
});

export const { useGetCouponsListQuery, useGetCouponByIdQuery } =
  couponsListSlice;
const selectCouponFilter = (state) => state.couponFilter;

const selectCouponsListResult = createSelector(
  [selectCouponFilter, (state) => state],
  (filter, state) =>
    couponsListSlice.endpoints.getCouponsList.select({
      search: filter.search,
      status: filter.status,
    })(state)
);

const selectCouponsListData = createSelector(
  [selectCouponsListResult],
  (result) => result?.data ?? initialState
);

export const {
  selectAll: selectCoupons,
  selectById: selectCouponById,
  selectIds: selectCouponIds,
} = couponsListAdapter.getSelectors((state) => selectCouponsListData(state));
